import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { CreateLineRequest } from "@opus-dt/shared-types";
import type {
  LineSummary,
  MachineDetail,
  MachineEventSummary,
  LineManualEntry,
  LineLoss,
} from "../../types/lines.types.js";
import { useDraftStore } from "./draft/useLineDraftState";
import { draftMachinesToDetails, isDraftLineId } from "./canvas/draft-canvas-adapter";

export type {
  LineSummary,
  MachineDetail,
  MachineEventSummary,
  LineManualEntry,
  LineLoss,
} from "../../types/lines.types.js";

const QK = ["lines"] as const;

export function useLines() {
  return useQuery<LineSummary[]>({
    queryKey: QK,
    queryFn: () => api.get<LineSummary[]>("/api/v1/lines"),
  });
}

export function useLine(id: string) {
  return useQuery<LineSummary>({
    queryKey: ["lines", id],
    queryFn: () => api.get<LineSummary>(`/api/v1/lines/${id}`),
    enabled: !!id,
  });
}

export function useLineMachines(
  lineId: string,
  refetchInterval?: number
): UseQueryResult<MachineDetail[]> {
  const isDraft = isDraftLineId(lineId);
  const draftMachines = useDraftStore((s) => s.machines);
  const draftData = useMemo<MachineDetail[]>(() => {
    if (!isDraft) return [];
    return draftMachinesToDetails({
      header: { name: "", description: "", responsible: "", productionUnit: "" },
      canvas: { nodes: [], edges: [] },
      machines: draftMachines,
      dirty: false,
    });
  }, [isDraft, draftMachines]);

  const serverQuery = useQuery<MachineDetail[]>({
    queryKey: ["lines", lineId, "machines"],
    queryFn: () => api.get<MachineDetail[]>(`/api/v1/lines/${lineId}/machines`),
    enabled: !!lineId && !isDraft,
    refetchInterval,
  });

  if (isDraft) {
    return {
      data: draftData,
      isLoading: false,
      isFetching: false,
      isError: false,
      isSuccess: true,
      error: null,
      refetch: () => Promise.resolve({ data: draftData }),
      status: "success",
    } as unknown as UseQueryResult<MachineDetail[]>;
  }

  return serverQuery;
}

export function useLineEvents(lineId: string, limit = 20, refetchInterval = 30_000) {
  return useQuery<MachineEventSummary[]>({
    queryKey: ["lines", lineId, "events", limit],
    // Review item 6 — uma única query por linha (backend filtra por `machine.lineId` e já ordena
    // por startAt desc), substituindo o fan-out 1-request-por-máquina + sort/slice no client.
    queryFn: () =>
      api.get<MachineEventSummary[]>(`/api/v1/machine-events?lineId=${lineId}&limit=${limit}`),
    enabled: !!lineId,
    refetchInterval,
  });
}

export function useLineManualEntries(lineId: string) {
  return useQuery<LineManualEntry[]>({
    queryKey: ["lines", lineId, "manual-entries"],
    queryFn: () =>
      api.get<LineManualEntry[]>(`/api/v1/data-imports?lineId=${lineId}&origin=MANUAL`),
    enabled: !!lineId,
    staleTime: 60_000,
  });
}

export function useLineLosses(lineId: string) {
  return useQuery<LineLoss[]>({
    queryKey: ["lines", lineId, "losses"],
    queryFn: () => api.get<LineLoss[]>(`/api/v1/lines/${lineId}/production-losses`),
    enabled: !!lineId,
    staleTime: 30_000,
  });
}

export interface RegisterLossInput {
  lossType: "REJECT" | "WASTE" | "REWORK";
  quantity: number;
  reason?: string;
  machineId?: string;
}

export function useRegisterLoss(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RegisterLossInput) =>
      api.post<LineLoss>(`/api/v1/lines/${lineId}/production-losses`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lines", lineId, "losses"] });
      void qc.invalidateQueries({ queryKey: ["lines", lineId] });
      void qc.invalidateQueries({ queryKey: ["oee", lineId] });
    },
    // Tratado localmente em LossForm.tsx, evita toast duplicado.
    meta: { skipErrorToast: true },
  });
}

export function useCreateLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLineRequest) => api.post<LineSummary>("/api/v1/lines", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useDeleteLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      api.delete(`/api/v1/lines/${id}`, {
        headers: force ? { "X-Force-Delete": "true" } : undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    // Tratado localmente em pages/Lines/index.tsx (inclui caso HAS_HISTORY/"Forçar").
    meta: { skipErrorToast: true },
  });
}
